import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFloating, autoUpdate, offset, flip, shift, hide, type Placement } from '@floating-ui/react';
import { useLingui } from '@lingui/react/macro';
import classnames from 'src/util/classnames';
import Icon from 'src/components/Icon';

function resolveTarget(target: string | React.RefObject<Element> | Element | null | undefined): Element | null {
  if (!target) return null;
  if (typeof target === 'string') {
    return document.getElementById(target) ?? document.querySelector(`#${target}`) ?? document.querySelector(target);
  }
  if ('current' in target) return target.current;
  return target as Element;
}

type TooltipVariant = 'light' | 'dark';

interface TooltipCoreProps {
  placement?: Placement;
  fade?: boolean;
  className?: string;
  children?: ReactNode;
  /** Visual style. 'light' (default) — white bg, dark text. 'dark' — black bg, white text. */
  variant?: TooltipVariant;
}

type TooltipContentProps = TooltipCoreProps & {
  anchorEl: Element | null;
  isOpen: boolean;
  /** When set, renders a close button inside the tooltip that calls this. */
  onClose?: () => void;
};

function TooltipContent({ anchorEl, isOpen, placement = 'top', className, children, variant = 'light', onClose }: TooltipContentProps) {
  const { t } = useLingui();
  // autoUpdate re-positions on scroll of every overflow ancestor, window resize and
  // element resize; hide() reports when the trigger is clipped out of its scroll container.
  const { refs, floatingStyles, middlewareData } = useFloating({
    open: isOpen,
    placement,
    elements: { reference: anchorEl ?? undefined },
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip(), shift({ padding: 4 }), hide()],
  });

  if (!isOpen || !anchorEl) return null;

  const referenceHidden = middlewareData.hide?.referenceHidden;

  return createPortal(
    <div
      ref={refs.setFloating}
      style={{ ...floatingStyles, zIndex: 1080, visibility: referenceHidden ? 'hidden' : 'visible' }}
      className={classnames('tooltip show bs-tooltip-auto', variant === 'dark' ? 'tooltip-dark' : 'tooltip-light', className)}
      role="tooltip"
    >
      <div className="tooltip-arrow" />
      <div className="tooltip-inner">
        {onClose ? (
          <div className="flex items-start gap-2">
            <div className="flex-1">{children}</div>
            <button
              type="button"
              className="-mr-1 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0.5 leading-none opacity-60 hover:opacity-100"
              aria-label={t`Close tooltip`}
              title={t`Close tooltip`}
              onClick={onClose}
            >
              <Icon icon="X" weight="regular" size={14} />
            </button>
          </div>
        ) : (
          children
        )}
      </div>
    </div>,
    document.body,
  );
}

// ---------- Controlled Tooltip ----------

interface TooltipProps extends TooltipCoreProps {
  isOpen?: boolean;
  toggle?: () => void;
  target: string | React.RefObject<Element> | Element;
  autohide?: boolean;
  trigger?: string;
}

export function Tooltip({ isOpen = false, toggle, target, placement, fade, className, children, variant }: TooltipProps) {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  useEffect(() => {
    setAnchorEl(resolveTarget(target));
  }, [target]);

  return <TooltipContent anchorEl={anchorEl} isOpen={isOpen} placement={placement} className={className} variant={variant}>{children}</TooltipContent>;
}

// ---------- Uncontrolled Tooltip ----------

interface UncontrolledTooltipProps extends TooltipCoreProps {
  target: string | React.RefObject<Element> | Element;
  autohide?: boolean;
  trigger?: string;
}

export function UncontrolledTooltip({
  target,
  trigger = 'hover focus',
  autohide = true,
  placement,
  fade,
  className,
  children,
  variant,
}: UncontrolledTooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const el = resolveTarget(target);
    if (!el) return;
    setAnchorEl(el);

    const show = () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
      setIsOpen(true);
    };
    const hide = () => {
      if (autohide) {
        hideTimer.current = setTimeout(() => setIsOpen(false), 0);
      }
    };

    const toggleOpen = () => setIsOpen(v => !v);

    const triggers = trigger.split(' ');
    if (triggers.includes('hover')) {
      el.addEventListener('mouseenter', show);
      el.addEventListener('mouseleave', hide);
    }
    if (triggers.includes('focus')) {
      el.addEventListener('focus', show);
      el.addEventListener('blur', hide);
    }
    if (triggers.includes('click')) {
      el.addEventListener('click', toggleOpen);
    }

    return () => {
      el.removeEventListener('click', toggleOpen);
      el.removeEventListener('mouseenter', show);
      el.removeEventListener('mouseleave', hide);
      el.removeEventListener('focus', show);
      el.removeEventListener('blur', hide);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [target, trigger, autohide]);

  // Click-toggled tooltips don't close on their own, so give users an explicit way out.
  const isClickTriggered = trigger.split(' ').includes('click');
  const close = useCallback(() => setIsOpen(false), []);

  return (
    <TooltipContent
      anchorEl={anchorEl}
      isOpen={isOpen}
      placement={placement}
      className={className}
      variant={variant}
      onClose={isClickTriggered ? close : undefined}
    >
      {children}
    </TooltipContent>
  );
}
